import React, { useState } from 'react';
import {
  Alert, Box, Button, Typography,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import type { ProjectionConflictView } from '../../../../services/LaborManagementService/types/laborManagementTypes';

interface StaleSaveAlertProps {
  conflict: ProjectionConflictView;
  onReload: () => void;
  onSaveAnyway: () => void;
}

// `lastSavedBy` is null on every week last saved before the service started recording savers.
// This fallback keeps the message readable instead of a dangling "saved by" with nothing after it.
const saverLabel = (lastSavedBy?: string | null) => (lastSavedBy ? lastSavedBy : 'Someone else');

const StaleSaveAlert: React.FC<StaleSaveAlertProps> = ({ conflict, onReload, onSaveAnyway }) => {
  const [reloadOpen, setReloadOpen] = useState(false);
  const [saveAnywayOpen, setSaveAnywayOpen] = useState(false);
  const savedAt = new Date(conflict.lastSavedAt).toLocaleString();
  const saver = saverLabel(conflict.lastSavedBy);

  return (
    <>
      <Alert data-testid="stale-save-alert" severity="warning">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          This copy of the week is out of date.
        </Typography>
        <Typography variant="body2">
          {saver} saved this week on {savedAt}.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button size="small" sx={{ textTransform: 'none' }} onClick={() => setReloadOpen(true)}>
            Reload
          </Button>
          <Button size="small" sx={{ textTransform: 'none' }} onClick={() => setSaveAnywayOpen(true)}>
            Save anyway
          </Button>
        </Box>
      </Alert>

      <Dialog open={reloadOpen} onClose={() => setReloadOpen(false)}>
        <DialogTitle sx={{ fontWeight: 600 }}>Discard your unsaved edits?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Reloading discards the edits on this screen and shows {saver}&apos;s values instead.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReloadOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => { setReloadOpen(false); onReload(); }}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Discard and reload
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={saveAnywayOpen} onClose={() => setSaveAnywayOpen(false)}>
        <DialogTitle sx={{ fontWeight: 600 }}>Replace the newer save?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This replaces {saver}&apos;s save with your values.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSaveAnywayOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => { setSaveAnywayOpen(false); onSaveAnyway(); }}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Replace and save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StaleSaveAlert;
